import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import useTranslation from 'Hooks/useTranslation';
import { UserEvaluationI, OpportunityRequestI } from 'types';
import { resolveEvaluationStatus, EvaluationStatusKind } from './evaluationStatus';

interface Props {
  open: boolean;
  onClose: () => void;
  courseName: string;
  evaluations: UserEvaluationI[];
  loading: boolean;
  error: string | null;
}

const STATUS_LABEL_KEY: Record<EvaluationStatusKind, string> = {
  notTaken: 'status_not_taken',
  passed: 'status_passed',
  pending: 'status_pending',
  failed: 'status_failed',
};

const OPPORTUNITY_LABEL_KEY: Record<OpportunityRequestI['status'], string> = {
  pending: 'opportunity_pending',
  approved: 'opportunity_approved',
  rejected: 'opportunity_rejected',
  used: 'opportunity_used',
};

const chipStyle = (background: string) => ({
  background,
  fontWeight: 'bold' as const,
});

export default function EmployeeCourseEvaluationsModal({
  open,
  onClose,
  courseName,
  evaluations,
  loading,
  error,
}: Props) {
  const { t2 } = useTranslation('employees_courses_report');

  const sortedEvaluations = [...evaluations].sort((a, b) => a.order - b.order);

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Typography color="error" sx={{ py: 2, textAlign: 'center' }}>
          {t2('evaluations_error')}
        </Typography>
      );
    }

    if (sortedEvaluations.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          {t2('no_evaluations')}
        </Typography>
      );
    }

    return (
      <Stack divider={<Divider flexItem />} spacing={2}>
        {sortedEvaluations.map(evaluation => {
          const status = resolveEvaluationStatus(evaluation);
          const statusLabel =
            status.kind === 'passed' || status.kind === 'failed'
              ? `${t2(STATUS_LABEL_KEY[status.kind])} ${status.grade} / 100`
              : t2(STATUS_LABEL_KEY[status.kind]);

          const hasAttempts =
            typeof evaluation.attemptsCount === 'number' && typeof evaluation.maxAttempts === 'number';

          return (
            <Box key={evaluation.lessonId}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {evaluation.name}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {`${t2('attempts_label')}: ${hasAttempts ? `${evaluation.attemptsCount} / ${evaluation.maxAttempts}` : '—'}`}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip label={statusLabel} sx={chipStyle(status.color)} />
                {evaluation.opportunityRequest != null && (
                  <Chip
                    variant="outlined"
                    label={t2(OPPORTUNITY_LABEL_KEY[evaluation.opportunityRequest.status])}
                  />
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t2('modal_title', [courseName])}</DialogTitle>
      <DialogContent dividers>{renderBody()}</DialogContent>
    </Dialog>
  );
}
